import { Alert, Button, Flex, List, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DuplicateMatch } from './api';

const { Text } = Typography;

const MATCH_REASON: Record<DuplicateMatch['matchedOn'], string> = {
  phone: 'customers.matchedOn.phone',
  idNumber: 'customers.matchedOn.idNumber',
  email: 'customers.matchedOn.email',
  'name+postcode': 'customers.matchedOn.namePostcode',
};

/**
 * Renders the 409 payload as two real choices rather than a red toast.
 *
 * The API returns the matches and WHY each matched precisely so this screen can
 * offer "open this customer" or "this is a different person". A duplicate
 * warning with no way to act on it just teaches staff to click past it, which
 * defeats the check.
 */
export function DuplicateWarning({
  matches,
  onOpenMatch,
  onConfirmDifferent,
}: {
  matches: DuplicateMatch[];
  onOpenMatch: (uid: string) => void;
  onConfirmDifferent: () => void;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <Alert
      type="warning"
      showIcon
      message={t('customers.duplicateTitle')}
      description={
        <Flex vertical gap={12}>
          <Text type="secondary">{t('customers.duplicateHelp')}</Text>
          <List
            size="small"
            dataSource={matches}
            renderItem={(match) => (
              <List.Item
                actions={[
                  <Button
                    key="open"
                    type="link"
                    onClick={() => {
                      onOpenMatch(match.uid);
                    }}
                  >
                    {t('customers.openMatch')}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={match.name}
                  description={`${match.phone} · ${t(MATCH_REASON[match.matchedOn])}`}
                />
              </List.Item>
            )}
          />
          <Button onClick={onConfirmDifferent}>{t('customers.differentPerson')}</Button>
        </Flex>
      }
    />
  );
}
